/**
 * perfMonitor — lightweight, opt-out perf breadcrumbs.
 *
 * Purpose: surface the "snappy at first, slow after a while" pattern
 * without needing a full DevTools profile every time. Logs heap +
 * DOM-node + listener counts to the console at a low cadence, plus
 * a small set of named breadcrumb events (file switch, project
 * change, view switch) so the timeline of growth is readable.
 *
 * Console output is prefixed `[PERF]` so it can be filtered out / in:
 *   DevTools → Console → filter `[PERF]`
 *
 * Usage:
 *   import { startPerfMonitor, logPerfEvent } from './utils/perfMonitor';
 *   startPerfMonitor();  // call once from main.jsx
 *   logPerfEvent('file:open', { name });  // sprinkle at key spots
 *
 * Also exposes `window.pidlyPerf` for on-demand access from DevTools:
 *   pidlyPerf.snap()      → log a snapshot right now
 *   pidlyPerf.history()   → recent samples
 *   pidlyPerf.stop()      → halt the interval
 *   pidlyPerf.start()     → restart the interval
 *   pidlyPerf.event(n, d) → log a custom breadcrumb
 */

// Sample cadence. 30s is light enough to ignore but frequent enough
// that 10 minutes of work yields ~20 samples — a usable trend.
const SAMPLE_INTERVAL_MS = 30_000;

// Cap the in-memory history so the monitor itself doesn't become a
// leak. Older samples roll off when the cap is reached.
const HISTORY_CAP = 200;

const state = {
  intervalId: null,
  history: [],
  startedAt: null,
};

// Best-effort heap reading. performance.memory only exists on
// Chromium-family browsers (and only when the page is loaded normally
// — not under iframes / cross-origin). Returns null when unavailable
// so the formatter skips heap fields cleanly.
function readHeap() {
  const m = typeof performance !== 'undefined' && performance.memory ? performance.memory : null;
  if (!m) return null;
  return {
    usedMB: Math.round(m.usedJSHeapSize / 1024 / 1024),
    totalMB: Math.round(m.totalJSHeapSize / 1024 / 1024),
    limitMB: Math.round(m.jsHeapSizeLimit / 1024 / 1024),
  };
}

// DOM nodes are a decent proxy for visual-state growth. Canvases,
// orphaned region overlays, dialog ghosts all push this up. Cheap
// to query (single getElementsByTagName('*').length call).
function readDom() {
  if (typeof document === 'undefined') return null;
  return {
    nodes: document.getElementsByTagName('*').length,
    canvases: document.getElementsByTagName('canvas').length,
  };
}

// Total addEventListener-installed listeners — only counts what the
// instrumented monkey-patch saw. Doesn't catch listeners added before
// startPerfMonitor() runs, but that's a tiny fraction.
const listenerCounts = {
  added: 0,
  removed: 0,
  // Per target-type/event: separate add + remove tallies so net = a - r is
  // ACCURATE (the old single-counter-deleted-at-1 approach drifted and made
  // the hotspot table not add up to the real total).
  byKind: new Map(), // kind -> { a, r }
};

function bumpKind(kind, field) {
  let e = listenerCounts.byKind.get(kind);
  if (!e) { e = { a: 0, r: 0 }; listenerCounts.byKind.set(kind, e); }
  e[field] += 1;
}

// One-shot leak tracer. Arm with window.pidlyPerf.trace('HTMLDivElement:click')
// then reproduce — the next N addEventListener('click') on a div console.trace
// their call stack, naming the exact code creating the leaking container.
const traceState = { kind: null, left: 0 };

function instrumentListeners() {
  if (typeof window === 'undefined') return;
  if (window.__pidlyPerfPatched) return;
  window.__pidlyPerfPatched = true;
  const targets = [EventTarget.prototype];
  for (const proto of targets) {
    const origAdd = proto.addEventListener;
    const origRemove = proto.removeEventListener;
    proto.addEventListener = function (type, listener, options) {
      try {
        listenerCounts.added += 1;
        const kind = `${this?.constructor?.name || 'el'}:${type}`;
        bumpKind(kind, 'a');
        if (traceState.left > 0 && kind === traceState.kind && this !== document.body && this.id !== 'root') {
          traceState.left -= 1;
          // eslint-disable-next-line no-console
          console.trace(`[PERF trace] ${kind} added to`, this);
        }
      } catch { /* never throw from a hot patch */ }
      return origAdd.call(this, type, listener, options);
    };
    proto.removeEventListener = function (type, listener, options) {
      try {
        listenerCounts.removed += 1;
        bumpKind(`${this?.constructor?.name || 'el'}:${type}`, 'r');
      } catch { /* never throw from a hot patch */ }
      return origRemove.call(this, type, listener, options);
    };
  }
}

// Build a snapshot object. Kept small so it's cheap to keep in history.
function buildSample(label = 'tick') {
  return {
    t: Date.now(),
    label,
    sinceStartSec: state.startedAt ? Math.round((Date.now() - state.startedAt) / 1000) : 0,
    heap: readHeap(),
    dom: readDom(),
    listeners: {
      added: listenerCounts.added,
      removed: listenerCounts.removed,
      net: listenerCounts.added - listenerCounts.removed,
    },
  };
}

function formatSample(s) {
  const parts = [`+${s.sinceStartSec}s`, s.label];
  if (s.heap) parts.push(`heap ${s.heap.usedMB}/${s.heap.totalMB}MB`);
  if (s.dom) parts.push(`dom ${s.dom.nodes} (${s.dom.canvases} canvas)`);
  parts.push(`listeners +${s.listeners.added}/-${s.listeners.removed} net ${s.listeners.net}`);
  return parts.join(' · ');
}

function pushSample(label) {
  const s = buildSample(label);
  state.history.push(s);
  if (state.history.length > HISTORY_CAP) state.history.shift();
  console.log('[PERF]', formatSample(s));
  return s;
}

// A main-thread hang of half a second or more is logged as it ends, with
// its length, so a freeze report can be matched to a time. Long tasks under
// that are ordinary rendering and stay quiet.
const LONG_TASK_LOG_MS = 500;
let longTaskObserver = null;
function observeLongTasks() {
  if (longTaskObserver || typeof PerformanceObserver === 'undefined') return;
  try {
    longTaskObserver = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration < LONG_TASK_LOG_MS) continue;
        const who = e.attribution && e.attribution[0] ? `${e.attribution[0].containerType || ''} ${e.attribution[0].containerSrc || ''}`.trim() : '';
        console.log(`[PERF] main thread blocked ${Math.round(e.duration)}ms${who ? ` (${who})` : ''} at +${state.startedAt ? Math.round((Date.now() - state.startedAt) / 1000) : 0}s`);
      }
    });
    longTaskObserver.observe({ entryTypes: ['longtask'] });
  } catch { longTaskObserver = null; }
}

export function startPerfMonitor() {
  if (state.intervalId) return; // already running
  instrumentListeners();
  observeLongTasks();
  state.startedAt = Date.now();
  pushSample('start');
  state.intervalId = setInterval(() => pushSample('tick'), SAMPLE_INTERVAL_MS);
}

export function stopPerfMonitor() {
  if (state.intervalId) {
    clearInterval(state.intervalId);
    state.intervalId = null;
  }
}

// Named breadcrumb — call from app code at key moments so the
// console log line up against actions the user took. Doesn't snapshot
// unless the heap reading meaningfully changes between ticks.
export function logPerfEvent(name, details = null) {
  const label = details ? `${name} ${JSON.stringify(details)}` : name;
  pushSample(label);
}

// Listener LEAK breakdown — the kinds with the largest net (added − removed),
// which is exactly the leak. Shows added/removed/net so a genuine leaker
// (net keeps climbing) is obvious vs. churn (high a+r, net ~0).
export function logListenerHotspots(limit = 20) {
  const rows = Array.from(listenerCounts.byKind.entries())
    .map(([kind, { a, r }]) => ({ kind, added: a, removed: r, net: a - r }))
    .filter(row => row.net !== 0)
    .sort((a, b) => b.net - a.net)
    .slice(0, limit);
  console.table(rows);
  const total = rows.reduce((s, x) => s + x.net, 0);
  console.log(`[PERF] listener net across shown kinds: ${total} (global net ${listenerCounts.added - listenerCounts.removed})`);
  return rows;
}

// Expose to DevTools for ad-hoc inspection.
if (typeof window !== 'undefined') {
  window.pidlyPerf = {
    snap: (label = 'manual') => pushSample(label),
    history: () => [...state.history],
    stop: stopPerfMonitor,
    start: startPerfMonitor,
    event: logPerfEvent,
    listeners: logListenerHotspots,
    // Arm the one-shot tracer: pidlyPerf.trace('HTMLDivElement:click'), then
    // reproduce (open a file). The next few adds of that kind print their stack.
    trace: (kind, n = 4) => { traceState.kind = kind; traceState.left = n; console.log(`[PERF] tracing next ${n} "${kind}" adds`); },
  };
}
